import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    inject,
    viewChild
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { getOrElse } from 'ngx-remotedata';
import { Subscription } from 'rxjs';
import { AixModalComponent } from '@trade-platform/ui-components';
import {
    AixSanitizeDocumentPipe,
    AixSanitizePipe,
    isStyledDocument
} from '@trade-platform/ui-utils';
import { AcknowledgeMessage, AppState, SponsorMessage } from '@trade-platform/ui-shared';

/**
 * Shows sponsor event communications the user has not acknowledged yet, one at a time, as a modal
 * they cannot dismiss without clicking OK (trade-platform/platform-operations#15027).
 *
 * A modal rather than a browser pop-up deliberately: this must work without the user having granted
 * pop-up permission.
 *
 * A message that styles itself -- the VREC II fund closure notice is a full document, banner,
 * typography and all -- is rendered in its own frame so that styling survives. See `showNext`.
 */
@Component({
    selector: 'aix-announcements-modal',
    templateUrl: './announcements.html',
    styles: [
        `
            .announcement-frame {
                display: block;
                width: 100%;
                border: 0;
                background: transparent;
                /* Until the first measurement lands, and a floor for a message with no content. */
                min-height: 120px;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AixModalComponent, AixSanitizePipe, AixSanitizeDocumentPipe]
})
export class AixAnnouncementsModalComponent implements AfterViewInit, OnDestroy {
    private store = inject<Store<AppState>>(Store);
    private cd = inject(ChangeDetectorRef);

    announcementsModal = viewChild<AixModalComponent>('announcementsModal');

    /** The message currently on screen, or null when there is nothing to show. */
    message: SponsorMessage | null = null;

    /**
     * Whether the message on screen brings its own stylesheet, and so goes in a frame rather than
     * inline. Settled once per message, in `showNext`.
     */
    messageIsStyled = false;

    /** Height of the frame, measured from its content once that content has loaded. */
    frameHeight: number | null = null;

    /** The queue behind it, newest state from the store. */
    private queue: SponsorMessage[] = [];

    /** A message may be body-only; the modal still needs a heading. */
    static DEFAULT_TITLE = 'Important message';

    buttonOptions = ['OK'];

    private subscriptions: Subscription[] = [];

    /** Watches the framed document, which reflows once its webfonts arrive. */
    private frameResize: ResizeObserver | null = null;

    // ngAfterViewInit, not ngOnInit: the store emits synchronously on subscribe, and opening the
    // modal needs the viewChild to have resolved.
    ngAfterViewInit() {
        this.subscriptions.push(
            this.store.pipe(select(state => state.messaging)).subscribe(state => {
                this.queue = getOrElse(state, [] as SponsorMessage[]);

                // Only take from the queue when nothing is on screen. Advancing here instead would
                // be undone immediately: AixModalComponent.buttonClicked() emits optionSelected
                // and THEN calls closeModal(), so a modal opened from this subscription -- which
                // runs synchronously inside that emit -- is hidden the moment control returns.
                // Advancing happens in onCloseModal() instead, after that trailing close.
                if (!this.message) {
                    this.showNext();
                }
            })
        );
    }

    /**
     * Empty for a self-styled message: that document carries its own heading, and the modal's own
     * title bar would only repeat it above a dotted rule.
     */
    get modalTitle(): string {
        if (!this.message || this.messageIsStyled) {
            return '';
        }
        return this.message.title || AixAnnouncementsModalComponent.DEFAULT_TITLE;
    }

    /** Sizes the frame to its document, so the modal scrolls as one rather than nesting scrollbars. */
    onFrameLoad(event: Event) {
        const frame = event.target as HTMLIFrameElement;
        this.measureFrame(frame);

        // Webfonts and images land after load and change the height; follow them. Observing the
        // same element measureFrame measures, so the two cannot disagree about what changed.
        this.frameResize?.disconnect();
        const body = frame.contentDocument?.body;
        if (body && typeof ResizeObserver !== 'undefined') {
            this.frameResize = new ResizeObserver(() => this.measureFrame(frame));
            this.frameResize.observe(body);
        }
    }

    onOptionSelected() {
        if (!this.message) {
            return;
        }
        // The reducer drops the message optimistically, which is what advances the queue. A failed
        // acknowledgement is not re-shown now -- the server keeps serving it until one lands, so it
        // comes back on the next login rather than reappearing under the user's cursor.
        this.store.dispatch(new AcknowledgeMessage({ id: this.message.id }));
    }

    /** Fired by aix-modal after it closes itself, which is where the queue advances. */
    onCloseModal() {
        this.message = null;
        this.showNext();
    }

    private showNext() {
        this.message = this.queue.length ? this.queue[0] : null;
        this.messageIsStyled = isStyledDocument(this.message?.textHTML);

        // Belongs to the message just dismissed; the next frame measures itself on load.
        this.frameResize?.disconnect();
        this.frameResize = null;
        this.frameHeight = null;

        if (this.message) {
            this.announcementsModal()?.openModal();
        }
        this.cd.detectChanges();
    }

    private measureFrame(frame: HTMLIFrameElement) {
        // body, not documentElement: on the root element scrollHeight is max(viewport, content), and
        // the viewport here is the frame whose height we just set -- so a measurement could only
        // ever grow, and the reflow this follows (a webfont swapping in for a taller fallback)
        // usually shrinks. body is not the root, so it reports the content height either way; the
        // injected reset gives html/body auto height and no margins, which is what makes that true.
        //
        // Null whenever the frame is cross-origin, which it is not -- but jsdom has no layout, so
        // this is also the test path.
        const height = frame.contentDocument?.body?.scrollHeight;
        if (!height || height === this.frameHeight) {
            return;
        }
        this.frameHeight = height;
        this.cd.detectChanges();
    }

    ngOnDestroy() {
        this.frameResize?.disconnect();
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
